import { ChangeEvent, DragEvent, useState } from "react";
import { DocumentMinimalData } from "../../../../types";
import { enqueueSnackbar } from "../../../../components/GlobalSnackbar/snackbarStore";
import useGetDriveConfig from "../../../google-drive/api/useGetDriveConfig";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

type Props = {
  value: DocumentMinimalData | null;
  onChange: (value: DocumentMinimalData | null) => void;
};

export default function useDriveFileField(props: Props) {
  const [isDragOver, setIsDragOver] = useState(false);
  const { data: driveConfig, isLoading: isDriveLoading } = useGetDriveConfig();

  // Treat loading as "connected" to avoid flicker; falls back to a link only
  // when the space has confirmed no Drive config.
  const canUploadFiles = isDriveLoading || !!driveConfig;

  function setFile(file: File) {
    if (file.size > MAX_FILE_SIZE) {
      enqueueSnackbar(`Le fichier "${file.name}" est trop volumineux (max 10Mo).`, "error");
      return;
    }

    props.onChange({
      id: crypto.randomUUID(),
      name: file.name.replace(/\.[^.]+$/, ""),
      file_url: null,
      type: null,
      pendingFile: file,
    });
  }

  function handleFileInputChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
    e.target.value = '';
  }

  function handleDrop(e: DragEvent) {
    e.preventDefault();
    setIsDragOver(false);
    if (canUploadFiles && e.dataTransfer.files.length > 0) {
      setFile(e.dataTransfer.files[0]);
    }
  }

  function handleDragOver(e: DragEvent) {
    e.preventDefault();
    if (canUploadFiles) {
      setIsDragOver(true);
    }
  }

  function handleDragLeave() {
    setIsDragOver(false);
  }

  function handleLinkChange(url: string) {
    if (!url) {
      props.onChange(null);
      return;
    }

    props.onChange({
      id: props.value?.id ?? crypto.randomUUID(),
      name: url,
      file_url: url,
      type: props.value?.type ?? null,
      isLink: true,
    });
  }

  function clear() {
    props.onChange(null);
  }

  return {
    isDragOver,
    canUploadFiles,
    handleFileInputChange,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleLinkChange,
    clear,
  };
}
